// ---------------------------------------------------------------------
//  @ Role Permissions Effects
// ---------------------------------------------------------------------

import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AlertService } from 'app/services/alert.service';
import { LoggerService } from 'app/services/logger.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { RolePermissionsService } from '../services/role-permissions.service';
import * as RolePermissionsActions from './role-permissions.actions';
import {
    ApiRoleModulePermission,
    mapApiToFrontend,
} from './role-permissions.models';

@Injectable()
export class RolePermissionsEffects {
    private _actions$ = inject(Actions);
    private _store = inject(Store);
    private logger = inject(LoggerService);
    private _rolePermissionsService = inject(RolePermissionsService);
    private _alertService = inject(AlertService);

    messages = {
        getAllError: 'Error getting role permissions',
        createSuccess: 'Role permission created successfully',
        createError: 'Error creating role permission',
        bulkCreateSuccess: 'Role permissions created successfully',
        bulkCreateError: 'Error creating role permissions',
        updateSuccess: 'Role permission updated successfully',
        updateError: 'Error updating role permission',
        deleteSuccess: 'Role permission deleted successfully',
        deleteError: 'Error deleting role permission',
        bulkDeleteSuccess: 'Role permissions deleted successfully',
        bulkDeleteError: 'Error deleting role permissions',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get all role permissions
    getAllRolePermissions$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RolePermissionsActions.loadRolePermissions),
            switchMap(() => {
                return this._rolePermissionsService.getAll().pipe(
                    map((res: any) => {
                        // The API returns: { statusCode, message, return: Array }
                        const apiData = res.return || res.data || res;

                        if (!Array.isArray(apiData)) {
                            console.error('❌ API Data is not an array!', res);
                            return RolePermissionsActions.loadRolePermissionsSuccess(
                                {
                                    rolePermissions: [],
                                }
                            );
                        }

                        // Map each API item
                        const mappedData = apiData
                            .map((item: ApiRoleModulePermission) => {
                                try {
                                    return mapApiToFrontend(item);
                                } catch (error) {
                                    console.error(
                                        '❌ Error mapping item:',
                                        error,
                                        item
                                    );
                                    return null;
                                }
                            })
                            .filter((item) => item !== null);

                        // Group by role-module and combine permissions
                        const groupedMap = new Map<string, any>();

                        mappedData.forEach((item: any) => {
                            const key = `${item.role}|${item.module}`;

                            if (groupedMap.has(key)) {
                                // Add permission to existing entry
                                const existing = groupedMap.get(key);
                                if (
                                    !existing.permissions.includes(
                                        item.permissions[0]
                                    )
                                ) {
                                    existing.permissions.push(
                                        item.permissions[0]
                                    );
                                }
                            } else {
                                // Create new entry
                                groupedMap.set(key, { ...item });
                            }
                        });

                        const groupedData = Array.from(groupedMap.values());

                        console.log(
                            '✅ Grouped role-permissions data:',
                            groupedData
                        );
                        console.log(
                            '📊 Example item with permissions:',
                            groupedData[0]
                        );

                        return RolePermissionsActions.loadRolePermissionsSuccess(
                            {
                                rolePermissions: groupedData,
                            }
                        );
                    }),
                    catchError((error) => {
                        console.error('Error loading role permissions:', error);
                        return of(
                            RolePermissionsActions.loadRolePermissionsFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        );
                    })
                );
            })
        )
    );

    // Get by role
    getRolePermissionsByRole$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RolePermissionsActions.loadRolePermissionsByRole),
            switchMap((action) => {
                return this._rolePermissionsService
                    .getByRole(action.role as any)
                    .pipe(
                        map((res) => {
                            // Transform API response to frontend model
                            const apiData = res.data as any;
                            const mappedData = Array.isArray(apiData)
                                ? apiData.map((item: ApiRoleModulePermission) =>
                                      mapApiToFrontend(item)
                                  )
                                : [];

                            return RolePermissionsActions.loadRolePermissionsSuccess(
                                {
                                    rolePermissions: mappedData,
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                RolePermissionsActions.loadRolePermissionsFailure(
                                    {
                                        error:
                                            error?.error?.message ||
                                            this.messages.unknownError,
                                    }
                                )
                            )
                        )
                    );
            })
        )
    );

    // Create role permission
    createRolePermission$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RolePermissionsActions.createRolePermission),
            switchMap((action) => {
                return this._rolePermissionsService
                    .create(action.permission)
                    .pipe(
                        map((res) => {
                            // Transform API response to frontend model
                            const apiData = res.data as any;
                            const mappedData = apiData
                                ? mapApiToFrontend(apiData)
                                : apiData;

                            return RolePermissionsActions.createRolePermissionSuccess(
                                {
                                    rolePermission: mappedData,
                                    success: this.messages.createSuccess,
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                RolePermissionsActions.createRolePermissionFailure(
                                    {
                                        error:
                                            error?.error?.message ||
                                            this.messages.unknownError,
                                    }
                                )
                            )
                        )
                    );
            })
        )
    );

    // Bulk create
    bulkCreateRolePermissions$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RolePermissionsActions.bulkCreateRolePermissions),
            switchMap((action) => {
                return this._rolePermissionsService
                    .bulkCreate(action.permissions)
                    .pipe(
                        map((res) => {
                            // Transform API response to frontend model
                            const apiData = res.data as any;
                            const mappedData = Array.isArray(apiData)
                                ? apiData.map((item: ApiRoleModulePermission) =>
                                      mapApiToFrontend(item)
                                  )
                                : [];

                            return RolePermissionsActions.bulkCreateRolePermissionsSuccess(
                                {
                                    rolePermissions: mappedData,
                                    success: this.messages.bulkCreateSuccess,
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                RolePermissionsActions.bulkCreateRolePermissionsFailure(
                                    {
                                        error:
                                            error?.error?.message ||
                                            this.messages.unknownError,
                                    }
                                )
                            )
                        )
                    );
            })
        )
    );

    // Update role permission
    updateRolePermission$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RolePermissionsActions.updateRolePermission),
            switchMap((action) => {
                return this._rolePermissionsService
                    .update(action.id, action.permission)
                    .pipe(
                        map((res) => {
                            // Transform API response to frontend model
                            const apiData = res.data as any;
                            const mappedData = apiData
                                ? mapApiToFrontend(apiData)
                                : apiData;

                            return RolePermissionsActions.updateRolePermissionSuccess(
                                {
                                    rolePermission: mappedData,
                                    success: this.messages.updateSuccess,
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                RolePermissionsActions.updateRolePermissionFailure(
                                    {
                                        error:
                                            error?.error?.message ||
                                            this.messages.unknownError,
                                    }
                                )
                            )
                        )
                    );
            })
        )
    );

    // Delete role permission
    deleteRolePermission$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RolePermissionsActions.deleteRolePermission),
            switchMap((action) => {
                return this._rolePermissionsService.delete(action.id).pipe(
                    map((res) => {
                        return RolePermissionsActions.deleteRolePermissionSuccess(
                            {
                                id: action.id,
                                success: this.messages.deleteSuccess,
                            }
                        );
                    }),
                    catchError((error) =>
                        of(
                            RolePermissionsActions.deleteRolePermissionFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Bulk delete
    bulkDeleteRolePermissions$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RolePermissionsActions.bulkDeleteRolePermissions),
            switchMap((action) => {
                return this._rolePermissionsService.bulkDelete(action.ids).pipe(
                    map((res) => {
                        return RolePermissionsActions.bulkDeleteRolePermissionsSuccess(
                            {
                                ids: action.ids,
                                success: this.messages.bulkDeleteSuccess,
                            }
                        );
                    }),
                    catchError((error) =>
                        of(
                            RolePermissionsActions.bulkDeleteRolePermissionsFailure(
                                {
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                }
                            )
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Success/Error Handlers
    // -----------------------------------------------------------

    onCreateSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RolePermissionsActions.createRolePermissionSuccess),
            tap(({ success }) => {
                this._alertService.showAlert(success, 'success');
            }),
            map(() => RolePermissionsActions.loadRolePermissions())
        )
    );

    onBulkCreateSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RolePermissionsActions.bulkCreateRolePermissionsSuccess),
            tap(({ success }) => {
                this._alertService.showAlert(success, 'success');
            }),
            map(() => RolePermissionsActions.loadRolePermissions())
        )
    );

    onUpdateSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RolePermissionsActions.updateRolePermissionSuccess),
            tap(({ success }) => {
                this._alertService.showAlert(success, 'success');
            }),
            map(() => RolePermissionsActions.loadRolePermissions())
        )
    );

    onDeleteSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RolePermissionsActions.deleteRolePermissionSuccess),
            tap(({ success }) => {
                this._alertService.showAlert(success, 'success');
            }),
            map(() => RolePermissionsActions.loadRolePermissions())
        )
    );

    onBulkDeleteSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RolePermissionsActions.bulkDeleteRolePermissionsSuccess),
            tap(({ success }) => {
                this._alertService.showAlert(success, 'success');
            }),
            map(() => RolePermissionsActions.loadRolePermissions())
        )
    );

    onError$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    RolePermissionsActions.loadRolePermissionsFailure,
                    RolePermissionsActions.createRolePermissionFailure,
                    RolePermissionsActions.bulkCreateRolePermissionsFailure,
                    RolePermissionsActions.updateRolePermissionFailure,
                    RolePermissionsActions.deleteRolePermissionFailure,
                    RolePermissionsActions.bulkDeleteRolePermissionsFailure
                ),
                tap(({ error }) => {
                    this._alertService.showAlert(error, 'error');
                })
            ),
        { dispatch: false }
    );
}
